import {
  getGraphInterfaceWithModelInterfaces,
  type RawGraphNode2,
  type TraverseOptions,
} from '@ardoq/graph';
import { buildGraph } from 'modelInterface/graph/buildGraphForWorkspaceMode';
import { EnhancedScopeData, GraphModelShape } from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import { buildTraversalViewModel } from 'tabview/graphViews/traversalViewModel';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { uniq } from 'lodash';
import { componentUtils } from '@ardoq/scope-data';
import type { I18nLocale } from '@ardoq/i18n';

interface DependencyMapTraversalViewModelProperties {
  startSet: ArdoqId[];
  graphModel: GraphModelShape;
  /** these are the traversal options used before reference traversal occurs in the Dependency Map. They will be used to determine the startSet for the reference types menu. */
  baseTraverseOptions?: TraverseOptions;
  /** these are the user's traverse options which specify the degrees of traversal. */
  referenceTraverseOptions: TraverseOptions;
  scopeData: EnhancedScopeData;
  i18nLocale: I18nLocale;
  referenceFilterContext: ReferenceFilterContext;
}
const dependencyMapTraversalViewModel = ({
  startSet,
  graphModel,
  baseTraverseOptions,
  referenceTraverseOptions,
  scopeData,
  i18nLocale,
  referenceFilterContext,
}: DependencyMapTraversalViewModelProperties) => {
  // potential optimization: move this buildGraph call to viewModel$ and perform a two-stage buildGraph rather than leveraging sequentialTraverseOptions for reference traversal. that would eliminate the redundancy of this buildGraph call.

  /**
   * remove interface calls from viewModel scope and pass GraphContext
   * as argument to use scopeData utils.
   * Currently, the graphInterface is used to ensure that the graph can
   * be built with scopeData.
   */
  const graphInterface = getGraphInterfaceWithModelInterfaces();
  const { componentMap, groupMap } = buildGraph({
    graphInterface,
    viewId: ViewIds.DEPENDENCY_MAP_2,
    startSet,
    graph: graphModel,
    traverseOptions: baseTraverseOptions,
    i18nLocale,
    ctx: referenceFilterContext,
  });
  const componentIds = uniq(
    [...componentMap.values(), ...groupMap.values()]
      .filter((graphItem): graphItem is RawGraphNode2 & { modelId: string } =>
        Boolean(
          graphItem.modelId &&
          componentUtils.isComponent(scopeData, graphItem.modelId)
        )
      )
      .map(({ modelId }) => modelId)
  );
  return buildTraversalViewModel({
    graphInterface,
    graphModel,
    startSet: componentIds,
    traverseOptions: referenceTraverseOptions,
  });
};
export { dependencyMapTraversalViewModel };
